import React, { useEffect, useMemo, useState } from "react";
import {
  CAlert,
  CBadge,
  CButton,
  CCallout,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSwitch,
  CFormTextarea,
  CLoadingButton,
  CRow,
} from "@coreui/react-pro";
import {
  getAdminPlatformAccessStatus,
  saveAdminPlatformAccessRule,
} from "../../services/platformAccessApi";

const DEFAULT_TIMEZONE = "Europe/Berlin";
const ROLE_SCOPES = ["applicant", "employer"];

const DEFAULT_MESSAGES = {
  global: "Wegen Wartungsarbeiten ist die Plattform aktuell nicht verfuegbar. Bitte versuchen Sie es spaeter erneut.",
  applicant: "Der Zugang fuer Bewerber ist aktuell nicht verfuegbar. Bitte versuchen Sie es spaeter erneut.",
  employer: "Der Zugang fuer Unternehmen ist aktuell nicht verfuegbar. Bitte versuchen Sie es spaeter erneut.",
};

function emptyRule(scope, role = null) {
  return {
    scope,
    role,
    is_active: false,
    block_login: false,
    block_register: false,
    block_session_access: false,
    message: "",
    internal_note: "",
    starts_at_local: "",
    ends_at_local: "",
    timezone: DEFAULT_TIMEZONE,
  };
}

function pickRule(snapshot, scope, role = null) {
  const rules = Array.isArray(snapshot?.rules) ? snapshot.rules : [];
  return rules.find((rule) => rule.scope === scope && (scope === "global" ? true : rule.role === role)) || emptyRule(scope, role);
}

function summarizeStatus(snapshot) {
  const status = String(snapshot?.status || "open");
  if (status === "global_closed") {
    return {
      color: "danger",
      title: "Global geschlossen",
      text: "Mindestens eine globale Regel blockiert aktuell Login, Registrierung oder den Zugriff eingeloggter Nutzer.",
    };
  }
  if (status === "partially_closed") {
    const roles = (snapshot?.active_role_rules || []).map((item) => item.role).join(", ");
    return {
      color: "warning",
      title: "Teilweise geschlossen",
      text: roles
        ? `Aktive Rollensperren fuer: ${roles}. Nicht betroffene Rollen bleiben offen.`
        : "Mindestens eine Rollensperre ist aktiv.",
    };
  }
  return {
    color: "success",
    title: "Plattform offen",
    text: "Es gibt aktuell keine aktive Plattform-Zugangssperre fuer Login, Registrierung oder Session-Zugriff.",
  };
}

function normalizeRulePayload(rule, fallbackKey) {
  return {
    is_active: Boolean(rule.is_active),
    block_login: Boolean(rule.block_login),
    block_register: Boolean(rule.block_register),
    block_session_access: Boolean(rule.block_session_access),
    message: String(rule.message || "").trim() || DEFAULT_MESSAGES[fallbackKey],
    internal_note: String(rule.internal_note || "").trim(),
    starts_at_local: String(rule.starts_at_local || "").trim(),
    ends_at_local: String(rule.ends_at_local || "").trim(),
    timezone: String(rule.timezone || DEFAULT_TIMEZONE).trim() || DEFAULT_TIMEZONE,
  };
}

function RuleEditorCard({
  title,
  description,
  rule,
  onChange,
  onSave,
  saving,
  accentColor = "info",
}) {
  return (
    <CCard className="h-100 shadow-sm">
      <CCardHeader className="d-flex justify-content-between align-items-start gap-3">
        <div>
          <h3 className="h6 mb-1">{title}</h3>
          <p className="text-body-secondary mb-0 small">{description}</p>
        </div>
        <CBadge color={accentColor} shape="rounded-pill">
          {rule.is_active ? "Aktiv" : "Inaktiv"}
        </CBadge>
      </CCardHeader>
      <CCardBody className="d-flex flex-column gap-3">
        <CFormSwitch
          id={`${title}-active`}
          label="Sperre aktiv"
          checked={Boolean(rule.is_active)}
          onChange={(event) => onChange("is_active", event.target.checked)}
        />
        <CRow className="g-3">
          <CCol md={4}>
            <CFormSwitch
              id={`${title}-login`}
              label="Login sperren"
              checked={Boolean(rule.block_login)}
              onChange={(event) => onChange("block_login", event.target.checked)}
            />
          </CCol>
          <CCol md={4}>
            <CFormSwitch
              id={`${title}-register`}
              label="Registrierung sperren"
              checked={Boolean(rule.block_register)}
              onChange={(event) => onChange("block_register", event.target.checked)}
            />
          </CCol>
          <CCol md={4}>
            <CFormSwitch
              id={`${title}-session`}
              label="Zugriff eingeloggter Nutzer sperren"
              checked={Boolean(rule.block_session_access)}
              onChange={(event) => onChange("block_session_access", event.target.checked)}
            />
          </CCol>
        </CRow>

        <div>
          <CFormLabel htmlFor={`${title}-message`}>Nachricht</CFormLabel>
          <CFormTextarea
            id={`${title}-message`}
            rows={4}
            value={rule.message || ""}
            onChange={(event) => onChange("message", event.target.value)}
            placeholder="Hinweis fuer betroffene Nutzer"
          />
        </div>

        <div>
          <CFormLabel htmlFor={`${title}-note`}>Interne Notiz</CFormLabel>
          <CFormTextarea
            id={`${title}-note`}
            rows={3}
            value={rule.internal_note || ""}
            onChange={(event) => onChange("internal_note", event.target.value)}
            placeholder="Interne Ops-Notiz"
          />
        </div>

        <CRow className="g-3">
          <CCol md={6}>
            <CFormLabel htmlFor={`${title}-start`}>Startzeit</CFormLabel>
            <CFormInput
              id={`${title}-start`}
              type="datetime-local"
              value={rule.starts_at_local || ""}
              onChange={(event) => onChange("starts_at_local", event.target.value)}
            />
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor={`${title}-end`}>Endzeit</CFormLabel>
            <CFormInput
              id={`${title}-end`}
              type="datetime-local"
              value={rule.ends_at_local || ""}
              onChange={(event) => onChange("ends_at_local", event.target.value)}
            />
          </CCol>
        </CRow>

        <div className="d-flex justify-content-between align-items-center gap-3">
          <span className="small text-body-secondary">Zeitzone: {rule.timezone || DEFAULT_TIMEZONE}</span>
          <CLoadingButton color="dark" loading={saving} disabledOnLoading onClick={onSave}>
            Regel speichern
          </CLoadingButton>
        </div>
      </CCardBody>
    </CCard>
  );
}

export default function PlatformAccessAdminPanel() {
  const [snapshot, setSnapshot] = useState(null);
  const [forms, setForms] = useState({
    global: emptyRule("global"),
    applicant: emptyRule("role", "applicant"),
    employer: emptyRule("role", "employer"),
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const statusMeta = useMemo(() => summarizeStatus(snapshot), [snapshot]);

  const loadSnapshot = async () => {
    setLoading(true);
    setError("");
    try {
      const nextSnapshot = await getAdminPlatformAccessStatus();
      setSnapshot(nextSnapshot);
      setForms({
        global: pickRule(nextSnapshot, "global"),
        applicant: pickRule(nextSnapshot, "role", "applicant"),
        employer: pickRule(nextSnapshot, "role", "employer"),
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Der Plattformzugang konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshot();
  }, []);

  const updateForm = (key, field, value) => {
    setForms((current) => ({
      ...current,
      [key]: {
        ...current[key],
        [field]: value,
      },
    }));
  };

  const saveRule = async (key) => {
    setSavingKey(key);
    setError("");
    setSuccessMessage("");
    try {
      const target = key === "global"
        ? { scope: "global", role: null, fallbackKey: "global" }
        : { scope: "role", role: key, fallbackKey: key };
      await saveAdminPlatformAccessRule({
        scope: target.scope,
        role: target.role,
        payload: normalizeRulePayload(forms[key], target.fallbackKey),
      });
      setSuccessMessage(`Die Regel fuer ${key === "global" ? "die globale Sperre" : key} wurde gespeichert.`);
      await loadSnapshot();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Die Regel konnte nicht gespeichert werden.");
    } finally {
      setSavingKey("");
    }
  };

  const applyQuickAction = async (key, nextActive) => {
    const fallbackKey = key === "global" ? "global" : key;
    const current = forms[key];
    const nextRule = {
      ...current,
      is_active: nextActive,
      block_login: nextActive ? true : current.block_login,
      block_register: nextActive ? true : current.block_register,
      block_session_access: nextActive ? true : current.block_session_access,
      message: String(current.message || "").trim() || DEFAULT_MESSAGES[fallbackKey],
    };
    setForms((state) => ({ ...state, [key]: nextRule }));
    setSavingKey(`quick:${key}`);
    setError("");
    setSuccessMessage("");
    try {
      await saveAdminPlatformAccessRule({
        scope: key === "global" ? "global" : "role",
        role: key === "global" ? null : key,
        payload: normalizeRulePayload(nextRule, fallbackKey),
      });
      setSuccessMessage(nextActive
        ? `${key === "global" ? "Globale" : key} Sperre sofort aktiviert.`
        : `${key === "global" ? "Globale" : key} Sperre wieder geoeffnet.`);
      await loadSnapshot();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Die Sofortaktion konnte nicht gespeichert werden.");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <CCallout color={statusMeta.color}>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h3 className="h5 mb-1">Platform Access Gate</h3>
            <p className="mb-1 fw-semibold">{statusMeta.title}</p>
            <p className="mb-0 text-body-secondary">{statusMeta.text}</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <CBadge color={statusMeta.color} shape="rounded-pill">
              {String(snapshot?.status || "open").replace(/_/g, " ")}
            </CBadge>
            <CBadge color="dark" shape="rounded-pill">
              {snapshot?.bypass_active_count || 0} aktive Bypaesse
            </CBadge>
          </div>
        </div>
      </CCallout>

      {error ? <CAlert color="danger">{error}</CAlert> : null}
      {successMessage ? <CAlert color="success">{successMessage}</CAlert> : null}

      <CCard className="shadow-sm">
        <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h3 className="h6 mb-1">Sofortaktionen</h3>
            <p className="mb-0 small text-body-secondary">Schnelle Betriebsumschaltung fuer globale und rollenspezifische Sperren.</p>
          </div>
          <CButton color="secondary" variant="outline" onClick={loadSnapshot} disabled={loading}>
            Status neu laden
          </CButton>
        </CCardHeader>
        <CCardBody className="d-flex flex-wrap gap-2">
          <CButton color="danger" variant="outline" disabled={savingKey === "quick:global"} onClick={() => applyQuickAction("global", true)}>
            Plattform sofort global schliessen
          </CButton>
          <CButton color="success" variant="outline" disabled={savingKey === "quick:global"} onClick={() => applyQuickAction("global", false)}>
            Plattform wieder oeffnen
          </CButton>
          {ROLE_SCOPES.map((role) => (
            <React.Fragment key={role}>
              <CButton color="warning" variant="outline" disabled={savingKey === `quick:${role}`} onClick={() => applyQuickAction(role, true)}>
                {role} sofort schliessen
              </CButton>
              <CButton color="info" variant="outline" disabled={savingKey === `quick:${role}`} onClick={() => applyQuickAction(role, false)}>
                {role} wieder oeffnen
              </CButton>
            </React.Fragment>
          ))}
        </CCardBody>
      </CCard>

      {loading ? (
        <CCard className="shadow-sm">
          <CCardBody>Plattformzugang wird geladen...</CCardBody>
        </CCard>
      ) : (
        <CRow className="g-3">
          <CCol xl={4}>
            <RuleEditorCard
              title="Globale Sperre"
              description="Wirkt plattformweit. Admins bleiben immer ausgenommen."
              rule={forms.global}
              onChange={(field, value) => updateForm("global", field, value)}
              onSave={() => saveRule("global")}
              saving={savingKey === "global"}
              accentColor="danger"
            />
          </CCol>
          <CCol xl={4}>
            <RuleEditorCard
              title="Applicant"
              description="Gilt nur fuer Bewerber bei Registrierung, Login und geschuetztem Session-Zugriff."
              rule={forms.applicant}
              onChange={(field, value) => updateForm("applicant", field, value)}
              onSave={() => saveRule("applicant")}
              saving={savingKey === "applicant"}
              accentColor="warning"
            />
          </CCol>
          <CCol xl={4}>
            <RuleEditorCard
              title="Employer"
              description="Gilt nur fuer Unternehmen bei Registrierung, Login und geschuetztem Session-Zugriff."
              rule={forms.employer}
              onChange={(field, value) => updateForm("employer", field, value)}
              onSave={() => saveRule("employer")}
              saving={savingKey === "employer"}
              accentColor="info"
            />
          </CCol>
        </CRow>
      )}
    </div>
  );
}
